export interface PublicErrorResult {
  error: string;
  message: string;
}

const FALLBACK_ERROR_CODE = 'REQUEST_FAILED';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function safeMessage(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed && !trimmed.includes('<') && !trimmed.includes('>') ? trimmed : undefined;
}

/** Converts the API's typed public error envelope into the UI's established result shape. */
export function normalizePublicError(payload: unknown, fallbackMessage: string): PublicErrorResult {
  if (!isRecord(payload)) {
    return { error: FALLBACK_ERROR_CODE, message: fallbackMessage };
  }

  const rawError = payload.error;
  if (isRecord(rawError)) {
    return {
      error: safeMessage(rawError.code) ?? FALLBACK_ERROR_CODE,
      message: safeMessage(rawError.message) ?? fallbackMessage,
    };
  }

  if (typeof rawError === 'string') {
    return {
      error: rawError,
      message: safeMessage(payload.message) ?? safeMessage(rawError) ?? fallbackMessage,
    };
  }

  return { error: FALLBACK_ERROR_CODE, message: fallbackMessage };
}

export function publicErrorMessage(payload: unknown, fallbackMessage: string): string {
  return normalizePublicError(payload, fallbackMessage).message;
}

export async function readPublicError(
  response: Response,
  fallbackMessage: string,
): Promise<string> {
  try {
    return publicErrorMessage(await response.json(), fallbackMessage);
  } catch {
    return fallbackMessage;
  }
}

export async function readJsonOrThrow(
  response: Response,
  fallbackMessage: string,
): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new Error(fallbackMessage);
  }
}
